import React, { useEffect, useState } from 'react';
import { ReceiveCreditFields } from '@/components/customers/ReceiveCreditFields';
import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, DollarSign, Eye, FileText, Upload, Download, BookOpen, MessageCircle, ChevronDown, Sparkles, Building2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { CustomerReconcileDialog } from '@/components/customers/CustomerReconcileDialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTaxConfig } from '@/hooks/useTaxConfig';
import { cur, getCurrencySymbol } from '@/lib/currency';
import { ExportButtons } from '@/components/ExportButtons';
import BankAccountsEditor from '@/components/BankAccountsEditor';
import { AdvancedSearch, applySearchFilters } from '@/components/AdvancedSearch';
import { useBranchStore } from '@/stores';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';
import { PlatformIbanBanner } from '@/components/banking/PlatformIbanBanner';
import { BulkWhatsAppButton } from './customers/BulkWhatsAppButton';
import { MoversPanel } from '@/components/common/MoversPanel';
import { CustomersTour } from '@/components/customers/CustomersTour';
import { LedgerExportDialog } from '@/components/customers/LedgerExportDialog';
import { localToday, toLocalYmd } from '@/lib/dateRange';

const parseCSV = (text) => {
  // Handle BOM and normalize line endings
  const clean = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = clean.split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  // Detect delimiter (comma or semicolon)
  const delim = lines[0].includes(';') && !lines[0].includes(',') ? ';' : ',';

  // Parse header
  const headers = lines[0].split(delim).map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());

  return lines.slice(1).map(line => {
    // Split respecting quoted fields
    const vals = [];
    let current = '';
    let inQuotes = false;
    for (let ch of line) {
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === delim && !inQuotes) { vals.push(current.trim()); current = ''; }
      else { current += ch; }
    }
    vals.push(current.trim());

    const row = {};
    headers.forEach((h, i) => { row[h] = (vals[i] || '').replace(/^"|"$/g, ''); });
    return row;
  }).filter(row => Object.values(row).some(v => v));
};

export default function CustomersPage() {
  // iter617 — Use the tenant's effective tax config to render the
  // correct currency symbol (SR / £ / AED / etc.) wherever we show
  // credit balances. Previously hardcoded `${cur()} ` in 8 places.
  const { taxConfig } = useTaxConfig();
  const CUR = getCurrencySymbol(taxConfig?.currency || 'SAR');
  const [customers, setCustomers] = useState([]);
  const [balances, setBalances] = useState([]);
  // Cash Flow Movers — same UX as Banking Top Movers / Sales Movers.
  const [cashflowMovers, setCashflowMovers] = useState([]);
  const [showCustomersTour, setShowCustomersTour] = useState(false);
  const { branches, fetchBranches } = useBranchStore();
  const [searchFilters, setSearchFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [reconcileCustomer, setReconcileCustomer] = useState(null); // iter571
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [customerReport, setCustomerReport] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [receivingCustomer, setReceivingCustomer] = useState(null);
  const [formData, setFormData] = useState({ name: '', ref: '', branch_id: '', phone: '', email: '', bank_accounts: [], alt_names: [], is_business: false, vat_number: '' });
  const [receiveData, setReceiveData] = useState({ payment_mode: 'cash', amount: '', discount: '' });
  const [receiving, setReceiving] = useState(false);
  const [importing, setImporting] = useState(false);
  // iter420 — Ledger export dialog state
  const [ledgerExportOpen, setLedgerExportOpen] = useState(false);
  const [ledgerExportMode, setLedgerExportMode] = useState('all'); // 'single' | 'all'
  const [ledgerExportCustomer, setLedgerExportCustomer] = useState(null);
  const openLedgerExport = (mode, customer = null) => {
    setLedgerExportMode(mode);
    setLedgerExportCustomer(customer);
    setLedgerExportOpen(true);
  };

  useEffect(() => { fetchData(); }, []);

  const handleExportCSV = async (params = {}) => {
    try {
      // iter418 — Optional date range / month query params. Backend
      // narrows the sales aggregates and stamps the period in row-1
      // of the CSV plus the filename.
      const qs = new URLSearchParams();
      if (params.month) qs.set('month', params.month);
      if (params.date_from) qs.set('date_from', params.date_from);
      if (params.date_to) qs.set('date_to', params.date_to);
      const url = qs.toString() ? `/customers-export?${qs.toString()}` : '/customers-export';
      const res = await api.get(url, { responseType: 'blob' });
      const blob = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blob;
      const name = params.month
        ? `customers_${params.month}.csv`
        : (params.date_from || params.date_to)
          ? `customers_${params.date_from || 'start'}_to_${params.date_to || 'now'}.csv`
          : 'customers.csv';
      link.setAttribute('download', name);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('Customers exported');
    } catch { toast.error('Export failed'); }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (!rows.length) { toast.error('No data found in CSV'); setImporting(false); return; }
      const res = await api.post('/customers-import', { rows });
      toast.success(`Imported ${res.data.imported} customers (${res.data.skipped} skipped)`);
      if (res.data.errors?.length) toast.warning(`${res.data.errors.length} errors`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); e.target.value = ''; }
  };

  const fetchData = async () => {
    try {
      fetchBranches();
      const [custRes, balRes] = await Promise.all([
        api.get('/customers'), api.get('/customers-balance')
      ]);
      setCustomers(custRes.data?.data || custRes.data || []); setBalances(balRes.data);
      // Cash flow movers — silent degrade if endpoint missing or empty.
      api.get('/customers/cashflow-movers?window_days=7&limit=3')
        .then((r) => setCashflowMovers(r.data?.movers || []))
        .catch(() => setCashflowMovers([]));
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.is_business && (!formData.vat_number || formData.vat_number.length !== 15)) {
      toast.error('A business customer needs a valid 15-digit VAT number');
      return;
    }
    try {
      const payload = { ...formData, branch_id: formData.branch_id || null };
      if (editingCustomer) { await api.put(`/customers/${editingCustomer.id}`, payload); toast.success('Customer updated'); }
      else { await api.post('/customers', payload); toast.success('Customer added'); }
      setShowDialog(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleReceiveCredit = async (e) => {
    e.preventDefault();
    if (!receivingCustomer || receiving) return;
    if (receiveData.payment_mode === 'bank' && !receiveData.bank_account_id) { toast.error('Select the bank account that received the money'); return; }
    setReceiving(true);
    const clientRequestId = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
    const extra = { payment_date: receiveData.payment_date || undefined, received_by_branch_id: receiveData.received_by_branch_id || undefined,
      bank_account_id: receiveData.payment_mode === 'bank' ? receiveData.bank_account_id : undefined,
      reference: receiveData.payment_mode === 'bank' ? receiveData.reference : undefined, client_request_id: clientRequestId };
    try {
      const salesRes = await api.get('/sales');
      const allSales = salesRes.data?.data || salesRes.data || [];
      const custSales = allSales.filter(s => s.customer_id === receivingCustomer.id && (s.credit_amount - s.credit_received) > 0);
      if (custSales.length === 0) { toast.error('No pending credit sales'); return; }
      
      let remainingPay = parseFloat(receiveData.amount) || 0;
      let remainingDisc = parseFloat(receiveData.discount) || 0;
      
      for (const sale of custSales) {
        if (remainingPay <= 0 && remainingDisc <= 0) break;
        const saleRemaining = sale.credit_amount - sale.credit_received;
        const payAmount = Math.min(remainingPay, saleRemaining);
        const discAmount = Math.min(remainingDisc, saleRemaining - payAmount);
        if (payAmount > 0 || discAmount > 0) {
          await api.post(`/sales/${sale.id}/receive-credit`, { payment_mode: receiveData.payment_mode, amount: payAmount, discount: discAmount, ...extra });
        }
        remainingPay -= payAmount;
        remainingDisc -= discAmount;
      }
      const totalSettled = (parseFloat(receiveData.amount) || 0) + (parseFloat(receiveData.discount) || 0);
      toast.success(`${CUR}${totalSettled.toFixed(2)} settled (Payment: ${CUR} ${(parseFloat(receiveData.amount) || 0).toFixed(2)}, Discount: ${CUR} ${(parseFloat(receiveData.discount) || 0).toFixed(2)})`);
      setShowReceiveDialog(false);
      setReceiveData({ payment_mode: 'cash', amount: '', discount: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setReceiving(false); }
  };

  const handleEdit = (c) => {
    setEditingCustomer(c);
    setFormData({ name: c.name, ref: c.ref || '', branch_id: c.branch_id || '', phone: c.phone || '', email: c.email || '', bank_accounts: c.bank_accounts || [], alt_names: c.alt_names || [], is_business: c.is_business || false, vat_number: c.vat_number || '' });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete customer?')) {
      try { await api.delete(`/customers/${id}`); toast.success('Deleted'); fetchData(); }
      catch { toast.error('Failed'); }
    }
  };

  const resetForm = () => { setFormData({ name: '', ref: '', branch_id: '', phone: '', email: '', bank_accounts: [], alt_names: [], is_business: false, vat_number: '' }); setEditingCustomer(null); };
  const getBalance = (id) => balances.find(b => b.id === id) || {};

  const viewReport = async (customerId) => {
    try {
      const res = await api.get(`/customers/${customerId}/report`);
      setCustomerReport(res.data); setShowReportDialog(true);
    } catch { toast.error('Failed to load report'); }
  };

  const exportCustomerPDF = async (customerId) => {
    try {
      const res = await api.get(`/customers/${customerId}/report/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `customer_statement.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('Statement downloaded');
    } catch { toast.error('Failed'); }
  };

  // iter402 — One-tap WhatsApp share. Builds a `wa.me` deep-link with a
  // pre-filled message; user attaches the downloaded PDF on their side.
  // No WhatsApp Business API needed — works on every phone with WhatsApp.
  const shareStatementOnWhatsApp = async (customer) => {
    try {
      // Trigger the PDF download first so the user has the file ready.
      await exportCustomerPDF(customer.id);
    } catch { /* exportCustomerPDF already toasts */ }
    const phoneRaw = (customer.phone || '').replace(/[^\d]/g, '');
    if (!phoneRaw) {
      toast.error('Add a phone number to this customer first');
      return;
    }
    // Saudi default: prepend 966 if a 9-digit local number is given
    let phone = phoneRaw;
    if (phone.length === 9 && phone.startsWith('5')) phone = `966${phone}`;
    if (phone.length === 10 && phone.startsWith('05')) phone = `966${phone.slice(1)}`;
    const balance = getBalance(customer.id);
    const owed = Number(balance.credit_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const today = new Date().toLocaleDateString('en-GB');
    const tenantName = (typeof window !== 'undefined' && window.localStorage?.getItem('tenant_name')) || 'our company';
    const msg = `Hi ${customer.name},\n\nYour account statement from ${tenantName} as of ${today}:\nOutstanding balance: ${CUR} ${owed}\n\nPlease attach the PDF I just downloaded for full details. Reply if you have any questions.\n\nThanks!`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success('Opening WhatsApp — attach the PDF and send');
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  const totalCredit = balances.reduce((s, b) => s + (b.credit_balance || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* What is this page? — collapsible orientation help. */}
        <PageInfoCallout
          storageKey="customers"
          title="What is the Customers module?"
          tone="sky"
          onTour={() => setShowCustomersTour(true)}
        >
          <p>
            Manage your customer base and track <strong>credit balances</strong> in one place — from
            registering new customers (with optional credit limits and loyalty enrolment) to receiving
            payments via cash, bank, or card. Use <strong>Receive Payment</strong> on a customer card to
            allocate against oldest invoices first (FIFO). The <strong>Cash flow movers</strong> panel
            below highlights customers whose payments sped up or slowed down sharply over the last 7
            days — early warning on bad debts, or a cue to offer volume discounts to your top payers.
          </p>
        </PageInfoCallout>

        {/* iter403 — Inline IBAN suggestions just for customers. Surfaces
            unmatched bank IBANs whose description references a customer
            so users can teach the smart-match scorer in one click. */}
        <PlatformIbanBanner days={90} entityTypes="customer" testidPrefix="cust-iban" />

        <MoversPanel
          movers={cashflowMovers.map((m) => ({
            id: m.customer_id,
            name: m.name,
            current: m.current_amount,
            previous: m.previous_amount,
            delta: m.delta,
            direction: m.direction,
          }))}
          title="Cash flow movers · last 7 days vs prior 7 days"
          subtitle="spot fast / slow payers early"
          tone="sky"
          unit="SAR received"
          testidPrefix="customers"
          formatValue={(v) => Math.round(v).toLocaleString()}
        />

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-blue-200/50">
              <Plus size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="customers-page-title">Customers</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Manage customers and track balances</p>
            </div>
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            <PageTour storageKey="tour_customers" steps={[
              { target: '[data-testid="add-customer-button"]', title: 'Add Customer', content: 'Create customer profiles with contact details and branch assignment.' },
              { target: '[data-testid="customers-table"]', title: 'Customer List', content: 'View all customers with credit balances. Click Report to see transaction history.' },
              { target: '[data-testid="view-report-btn"]', title: 'Credit Reports', content: 'Generate detailed credit reports and receive payments from customers.' },
            ]} />
            <ExportButtons dataType="customers" />
            <BulkWhatsAppButton
              customers={customers}
              balances={balances}
              onShare={shareStatementOnWhatsApp}
            />
            {/* iter418 — Export menu with All-time / This month / Custom range options */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full h-9 text-xs" data-testid="export-csv-btn">
                  <Download size={14} className="mr-1.5" />Export CSV<ChevronDown size={12} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">By period</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleExportCSV({})} data-testid="export-all-time">
                  All time
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    const d = new Date();
                    handleExportCSV({ month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` });
                  }}
                  data-testid="export-this-month"
                >
                  This month
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    const d = new Date();
                    d.setMonth(d.getMonth() - 1);
                    handleExportCSV({ month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` });
                  }}
                  data-testid="export-last-month"
                >
                  Last month
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    const today = new Date();
                    const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
                    handleExportCSV({
                      date_from: toLocalYmd(start),
                      date_to: toLocalYmd(today),
                    });
                  }}
                  data-testid="export-last-90"
                >
                  Last 90 days
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleExportCSV({ date_from: `${new Date().getFullYear()}-01-01` })}
                  data-testid="export-ytd"
                >
                  Year to date
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    const fromStr = window.prompt('From date (YYYY-MM-DD):', localToday());
                    if (!fromStr) return;
                    const toStr = window.prompt('To date (YYYY-MM-DD):', localToday());
                    if (!toStr) return;
                    handleExportCSV({ date_from: fromStr, date_to: toStr });
                  }}
                  data-testid="export-custom-range"
                >
                  Custom range…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* iter420 — Bulk Export ALL customer ledgers by date range */}
            <Button
              variant="outline"
              size="sm"
              className="rounded-full h-9 text-xs"
              onClick={() => openLedgerExport('all')}
              data-testid="export-all-ledgers-btn"
              title="Export every customer's ledger filtered by date range — combined CSV, ZIP of CSVs, or ZIP of PDFs"
            >
              <BookOpen size={14} className="mr-1.5" />Export Ledgers
            </Button>
            <Button variant="outline" size="sm" className="rounded-full h-9 text-xs" disabled={importing} data-testid="import-csv-btn"
              onClick={() => document.getElementById('csv-import-input').click()}>
              <Upload size={14} className="mr-1.5" />{importing ? 'Importing...' : 'Import CSV'}
            </Button>
            <input id="csv-import-input" type="file" accept=".csv,.txt" onChange={handleImportCSV} className="hidden" />
            <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild><Button className="rounded-full" data-testid="add-customer-button-2"><Plus size={18} className="mr-2" />Add Customer</Button></DialogTrigger>
              <DialogContent data-testid="customer-dialog">
                <DialogHeader><DialogTitle className="font-outfit">{editingCustomer ? 'Edit' : 'Add'} Customer</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Name *</Label><Input value={formData.name} data-testid="customer-name-input" onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
                    <div><Label>Ref / Code</Label><Input value={formData.ref} data-testid="customer-ref-input" onChange={(e) => setFormData({ ...formData, ref: e.target.value })} placeholder="e.g. CUST-001" /></div>
                  </div>
                  <div><Label>Branch</Label>
                    <Select value={formData.branch_id || "all"} onValueChange={(v) => setFormData({ ...formData, branch_id: v === "all" ? "" : v })}>
                      <SelectTrigger data-testid="customer-branch-select"><SelectValue placeholder="All branches" /></SelectTrigger>
                      <SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Phone</Label><Input value={formData.phone} data-testid="customer-phone-input" onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
                  <div><Label>Email</Label><Input type="email" value={formData.email} data-testid="customer-email-input" onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>

                  {/* iter813 — Business customer → B2B ZATCA clearance invoices */}
                  <div className="rounded-xl border p-3.5 bg-muted/30 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 size={16} className="text-emerald-600" />
                        <div>
                          <Label className="cursor-pointer">Business customer (B2B)</Label>
                          <p className="text-[11px] text-muted-foreground">Issues a ZATCA standard (cleared) invoice with the buyer's VAT.</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.is_business}
                        onCheckedChange={(v) => setFormData({ ...formData, is_business: v, vat_number: v ? formData.vat_number : '' })}
                        data-testid="customer-is-business-switch"
                      />
                    </div>
                    {formData.is_business && (
                      <div>
                        <Label>VAT registration number *</Label>
                        <Input
                          value={formData.vat_number}
                          data-testid="customer-vat-input"
                          onChange={(e) => setFormData({ ...formData, vat_number: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                          placeholder="15-digit VAT number (e.g. 3XXXXXXXXXXXXX3)"
                          inputMode="numeric"
                        />
                        {formData.vat_number && formData.vat_number.length !== 15 && (
                          <p className="text-[11px] text-amber-600 mt-1">VAT number should be 15 digits.</p>
                        )}
                      </div>
                    )}
                  </div>

                  <BankAccountsEditor
                    value={formData.bank_accounts}
                    onChange={(arr) => setFormData({ ...formData, bank_accounts: arr })}
                    testidPrefix="cust-bank"
                  />
                  <div className="flex gap-3">
                    <Button type="submit" data-testid="submit-customer-button" className="rounded-full">{editingCustomer ? 'Update' : 'Add'} Customer</Button>
                    <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="rounded-full">Cancel</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-border hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Total Customers</p>
                  <p className="text-3xl font-bold font-outfit text-foreground mt-1">{customers.length}</p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-info/10 text-info flex items-center justify-center ring-1 ring-blue-100">
                  <Plus size={18} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Total Sales</p>
                  <p className="text-3xl font-bold font-outfit text-success mt-1">{CUR} {balances.reduce((s, b) => s + (b.total_sales || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-success/10 text-success flex items-center justify-center ring-1 ring-emerald-100">
                  <DollarSign size={18} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-warning/30 hover:shadow-md transition-shadow bg-warning/10">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-warning uppercase tracking-wider">Outstanding Credit</p>
                  <p className="text-3xl font-bold font-outfit text-warning mt-1">{CUR} {totalCredit.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-warning/20 text-warning flex items-center justify-center ring-1 ring-amber-200">
                  <DollarSign size={18} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-1 h-5 rounded-full bg-info" />
              <CardTitle className="font-outfit text-sm">All Customers</CardTitle>
              <div className="flex-1 h-px bg-muted" />
              <span className="text-[10px] text-muted-foreground font-medium">{customers.length} total</span>
            </div>
          </CardHeader>
          <CardContent>
            {/* Advanced Search */}
            <AdvancedSearch 
              onSearch={setSearchFilters}
              config={{
                searchFields: ['name', 'ref', 'phone', 'email'],
                placeholder: 'Search by name, ref, phone, email...',
                filters: [
                  { 
                    key: 'branch_id', 
                    label: 'Branch', 
                    type: 'select', 
                    options: branches.map(b => ({ value: b.id, label: b.name }))
                  },
                  { key: 'credit_balance', label: 'Credit Balance', type: 'range' }
                ]
              }}
              className="mb-4"
            />
            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <table className="w-full hidden md:table" data-testid="customers-table-2">
                <thead><tr className="border-b border-border">
                  <th className="text-left p-3 font-medium text-sm w-12">#</th>
                  <th className="text-left p-3 font-medium text-sm">Name</th>
                  <th className="text-left p-3 font-medium text-sm">Ref</th>
                  <th className="text-left p-3 font-medium text-sm">Branch</th>
                  <th className="text-left p-3 font-medium text-sm">Phone</th>
                  <th className="text-right p-3 font-medium text-sm">Total Sales</th>
                  <th className="text-right p-3 font-medium text-sm">Cash</th>
                  <th className="text-right p-3 font-medium text-sm">Bank</th>
                  <th className="text-right p-3 font-medium text-sm">Credit Balance</th>
                  <th className="text-right p-3 font-medium text-sm">Actions</th>
                </tr></thead>
                <tbody>
                  {applySearchFilters(customers.map(c => {
                    const bal = getBalance(c.id);
                    return { ...c, credit_balance: bal.credit_balance || 0, total_sales: bal.total_sales || 0 };
                  }), searchFilters).map((c, index) => {
                    const bal = getBalance(c.id);
                    const brName = branches.find(b => b.id === c.branch_id)?.name || 'All';
                    // iter590 — Quick Reconcile pill. Mirrors the iter589
                    // supplier-side pill but uses the iter582 discount-aware
                    // Outstanding formula so per-row gap matches the
                    // Customer Reconcile dialog math exactly.
                    const _totalSales = Number(bal.total_sales || 0);
                    const _outstandingC = Math.max(0, _totalSales - Number(bal.cash || 0) - Number(bal.bank || 0) - Number(bal.discount_received || 0));
                    const _creditC = Math.max(0, Number(bal.credit_balance || 0));
                    const _reconGapC = Math.abs(_outstandingC - _creditC);
                    return (
                      <tr key={c.id ?? `row-${index}`} className="border-b border-border hover:bg-secondary/50" data-testid="customer-row">
                        <td className="p-3 text-sm text-muted-foreground font-mono">{index + 1}</td>
                        <td className="p-3 text-sm font-medium">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span>{c.name}</span>
                            {_totalSales > 0 && (
                              _reconGapC < 1 ? (
                                <button
                                  type="button"
                                  onClick={() => setReconcileCustomer(c)}
                                  title="Outstanding matches Credit Balance — click to open the reconcile snapshot"
                                  className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                                  data-testid={`quick-recon-pill-customer-${c.id}`}
                                >
                                  <Sparkles size={9} /> In sync
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setReconcileCustomer(c)}
                                  title={`Outstanding (${CUR} ${_outstandingC.toFixed(2)}) ≠ Credit Balance (${CUR} ${_creditC.toFixed(2)}). Click to investigate.`}
                                  className={`inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full border transition-colors ${
                                    _reconGapC >= 100
                                      ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                                      : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
                                  }`}
                                  data-testid={`quick-recon-pill-customer-${c.id}`}
                                >
                                  Δ {_reconGapC.toLocaleString(undefined, { maximumFractionDigits: 0 })} · Reconcile
                                </button>
                              )
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground font-mono">{c.ref || '-'}</td>
                        <td className="p-3 text-sm">{brName}</td>
                        <td className="p-3 text-sm">{c.phone || '-'}</td>
                        <td className="p-3 text-sm text-right"> {CUR} {(bal.total_sales || 0).toFixed(2)}</td>
                        <td className="p-3 text-sm text-right text-cash"> {CUR} {(bal.cash || 0).toFixed(2)}</td>
                        <td className="p-3 text-sm text-right text-bank"> {CUR} {(bal.bank || 0).toFixed(2)}</td>
                        <td className="p-3 text-sm text-right">
                          {(bal.credit_balance || 0) > 0 ? <span className="font-bold text-warning"> {CUR} {bal.credit_balance.toFixed(2)}</span> : <span className="text-muted-foreground">$0.00</span>}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex gap-1 justify-end flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => viewReport(c.id)} className="h-8 text-xs" data-testid="view-report-btn-2"><BookOpen size={14} className="mr-1 hidden sm:inline" />Ledger</Button>
                            <Button size="sm" variant="ghost" onClick={() => exportCustomerPDF(c.id)} className="h-8" title="Download statement PDF (all-time)"><FileText size={14} /></Button>
                            {/* iter420 — Per-customer Export Ledger by date range */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openLedgerExport('single', c)}
                              className="h-8 text-primary"
                              title="Export this customer's ledger by date range (PDF or CSV)"
                              data-testid={`export-ledger-${c.id}`}
                            >
                              <Download size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => shareStatementOnWhatsApp(c)}
                              className="h-8 text-emerald-600 hover:text-emerald-700"
                              title={c.phone ? `Send statement to ${c.phone} via WhatsApp` : 'Add phone number first'}
                              data-testid={`whatsapp-statement-${c.id}`}
                            >
                              <MessageCircle size={14} />
                            </Button>
                            {(bal.credit_balance || 0) > 0 && (
                              <Button size="sm" variant="outline" onClick={() => { setReceivingCustomer({ ...c, credit_balance: bal.credit_balance }); setReceiveData({ payment_mode: 'cash', amount: '' }); setShowReceiveDialog(true); }} data-testid="receive-credit-btn" className="h-8 text-xs">
                                <DollarSign size={14} className="mr-1 hidden sm:inline" />Receive
                              </Button>
                            )}
                            {/* iter571 — one-click Reconcile */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setReconcileCustomer(c)}
                              className="h-8 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                              data-testid={`reconcile-customer-${c.id}`}
                              title="Inspect every sale + receipt for this customer"
                            >
                              <Sparkles size={14} className="hidden sm:inline mr-1" />Reconcile
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleEdit(c)} data-testid="edit-customer-button" className="h-8"><Edit size={14} /></Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id)} data-testid="delete-customer-button" className="h-8 text-error"><Trash2 size={14} /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {customers.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">No customers yet</td></tr>}
                </tbody>
              </table>
              {/* Mobile Cards */}
              <div className="md:hidden space-y-3" data-testid="customers-mobile-list">
                {applySearchFilters(customers.map(c => {
                  const bal = getBalance(c.id);
                  return { ...c, credit_balance: bal.credit_balance || 0, total_sales: bal.total_sales || 0 };
                }), searchFilters).map((c, index) => {
                  const bal = getBalance(c.id);
                  const brName = branches.find(b => b.id === c.branch_id)?.name || 'All';
                  // iter590 — Quick Reconcile pill (mobile mirror).
                  const _totalSalesM = Number(bal.total_sales || 0);
                  const _outstandingCM = Math.max(0, _totalSalesM - Number(bal.cash || 0) - Number(bal.bank || 0) - Number(bal.discount_received || 0));
                  const _creditCM = Math.max(0, Number(bal.credit_balance || 0));
                  const _reconGapCM = Math.abs(_outstandingCM - _creditCM);
                  return (
                    <div key={c.id ?? `mcard-${index}`} className="border rounded-xl p-3 dark:border-stone-700" data-testid={`customer-mobile-card-${c.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-semibold text-sm dark:text-white"><span className="text-muted-foreground font-mono mr-1.5">#{index + 1}</span>{c.name}</p>
                            {_totalSalesM > 0 && (
                              _reconGapCM < 1 ? (
                                <button
                                  type="button"
                                  onClick={() => setReconcileCustomer(c)}
                                  className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                                >
                                  <Sparkles size={9} /> In sync
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setReconcileCustomer(c)}
                                  className={`inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${
                                    _reconGapCM >= 100
                                      ? 'bg-rose-50 text-rose-700 border-rose-200'
                                      : 'bg-amber-50 text-amber-800 border-amber-200'
                                  }`}
                                >
                                  Δ {_reconGapCM.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </button>
                              )
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {c.ref && <span className="text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{c.ref}</span>}
                            <span className="text-[10px] text-muted-foreground">{brName}</span>
                            {c.phone && <span className="text-[10px] text-muted-foreground">{c.phone}</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          {(bal.credit_balance || 0) > 0 
                            ? <span className="font-bold text-sm text-warning">{CUR} {bal.credit_balance.toFixed(2)}</span>
                            : <span className="text-xs text-muted-foreground">No credit</span>
                          }
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(bal.total_sales || 0) > 0 && <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">Sales: {(bal.total_sales || 0).toFixed(0)}</span>}
                        {(bal.cash || 0) > 0 && <span className="px-2 py-0.5 rounded-full bg-success/10 text-success text-[10px] font-medium">Cash: {(bal.cash || 0).toFixed(0)}</span>}
                        {(bal.bank || 0) > 0 && <span className="px-2 py-0.5 rounded-full bg-info/10 text-info text-[10px] font-medium">Bank: {(bal.bank || 0).toFixed(0)}</span>}
                      </div>
                      <div className="flex gap-1 mt-2 pt-2 border-t border-border dark:border-stone-700">
                        <Button size="sm" variant="outline" onClick={() => viewReport(c.id)} className="h-7 text-[10px] flex-1" data-testid={`mobile-view-report-${c.id}`}>Ledger</Button>
                        {(bal.credit_balance || 0) > 0 && (
                          <Button size="sm" variant="outline" onClick={() => { setReceivingCustomer({ ...c, credit_balance: bal.credit_balance }); setReceiveData({ payment_mode: 'cash', amount: '' }); setShowReceiveDialog(true); }} className="h-7 text-[10px] flex-1 text-success" data-testid={`mobile-receive-${c.id}`}>Receive</Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(c)} className="h-7 text-[10px]">Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id)} className="h-7 text-[10px] text-error">Del</Button>
                      </div>
                    </div>
                  );
                })}
                {customers.length === 0 && <p className="text-center text-muted-foreground py-8">No customers yet</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Receive Credit Dialog */}
        <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
          <DialogContent data-testid="receive-credit-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Receive Credit - {receivingCustomer?.name}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Outstanding: <span className="font-bold text-warning"> {CUR} {receivingCustomer?.credit_balance?.toFixed(2)}</span></p>
            <form onSubmit={handleReceiveCredit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Payment Amount</Label><Input type="number" step="0.01" value={receiveData.amount} data-testid="receive-amount-input" onChange={(e) => setReceiveData({ ...receiveData, amount: e.target.value })} placeholder="0.00" /></div>
                <div><Label>Discount</Label><Input type="number" step="0.01" value={receiveData.discount} data-testid="receive-discount-input" onChange={(e) => setReceiveData({ ...receiveData, discount: e.target.value })} placeholder="0.00" /></div>
              </div>
              {(parseFloat(receiveData.amount) > 0 || parseFloat(receiveData.discount) > 0) && (
                <div className="p-3 bg-secondary/50 rounded-lg space-y-1 text-sm">
                  <div className="flex justify-between"><span>Payment:</span><span className="font-medium"> {CUR} {(parseFloat(receiveData.amount) || 0).toFixed(2)}</span></div>
                  {parseFloat(receiveData.discount) > 0 && <div className="flex justify-between"><span>Discount:</span><span className="font-medium text-error">-{CUR} {(parseFloat(receiveData.discount) || 0).toFixed(2)}</span></div>}
                  <div className="flex justify-between border-t pt-1 font-bold"><span>Total Settled:</span><span className="text-success"> {CUR} {((parseFloat(receiveData.amount) || 0) + (parseFloat(receiveData.discount) || 0)).toFixed(2)}</span></div>
                  <div className="flex justify-between text-xs text-muted-foreground"><span>Remaining after:</span><span> {CUR} {(receivingCustomer?.credit_balance - (parseFloat(receiveData.amount) || 0) - (parseFloat(receiveData.discount) || 0)).toFixed(2)}</span></div>
                </div>
              )}
              <div><Label>Payment Mode</Label>
                <Select value={receiveData.payment_mode} onValueChange={(v) => setReceiveData({ ...receiveData, payment_mode: v })}>
                  <SelectTrigger data-testid="receive-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent>
                </Select>
              </div>
              <ReceiveCreditFields data={receiveData} setData={setReceiveData} branches={branches} />
              <div className="flex gap-3">
                <Button type="submit" disabled={receiving} data-testid="submit-receive-button" className="rounded-full">{receiving ? 'Saving…' : 'Receive Payment'}</Button>
                <Button type="button" variant="outline" onClick={() => setShowReceiveDialog(false)} className="rounded-full">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Customer Report Dialog */}
        <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
          <DialogContent className="max-w-3xl max-h-[85dvh] overflow-y-auto" data-testid="customer-report-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit flex justify-between items-center">
                <span>Customer Ledger - {customerReport?.customer?.name}</span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => shareStatementOnWhatsApp(customerReport?.customer)}
                    className="rounded-xl text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    data-testid="whatsapp-statement-dialog"
                  >
                    <MessageCircle size={14} className="mr-2" />WhatsApp
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => exportCustomerPDF(customerReport?.customer?.id)} className="rounded-xl"><FileText size={14} className="mr-2" />Export PDF</Button>
                </div>
              </DialogTitle>
            </DialogHeader>
            {customerReport && (
              <div className="space-y-4">
                {/* iter405 — Inline IBAN suggestions filtered to this
                    customer only — surfaces unmatched bank txns
                    referencing this customer's name. */}
                <PlatformIbanBanner
                  days={90}
                  entityTypes="customer"
                  entityId={customerReport?.customer?.id}
                  testidPrefix="ledger-cust-iban"
                />
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  <div className="p-3 bg-success/10 rounded-xl"><div className="text-xs text-muted-foreground">Total Purchases</div><div className="text-lg font-bold text-success">{CUR} {customerReport.total.toFixed(2)}</div></div>
                  <div className="p-3 bg-error/10 rounded-xl"><div className="text-xs text-muted-foreground">Total Discount</div><div className="text-lg font-bold text-error">{CUR} {customerReport.total_discount.toFixed(2)}</div></div>
                  <div className="p-3 bg-warning/10 rounded-xl"><div className="text-xs text-muted-foreground">Credit Balance</div><div className="text-lg font-bold text-warning">{CUR} {customerReport.credit_balance.toFixed(2)}</div></div>
                  <div className="p-3 bg-muted/50 rounded-xl"><div className="text-xs text-muted-foreground">Transactions</div><div className="text-lg font-bold">{customerReport.count}</div></div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full"><thead><tr className="border-b"><th className="text-left p-2 text-xs font-medium">Date</th><th className="text-left p-2 text-xs font-medium">Branch</th><th className="text-right p-2 text-xs font-medium">Amount</th><th className="text-right p-2 text-xs font-medium">Discount</th><th className="text-left p-2 text-xs font-medium">Payment</th><th className="text-right p-2 text-xs font-medium">Credit Due</th></tr></thead>
                  <tbody>{customerReport.purchases.map((p, i) => (
                    <React.Fragment key={i}>
                    <tr className="border-b hover:bg-muted/50">
                      <td className="p-2 text-xs">{new Date(p.date).toLocaleDateString()}</td>
                      <td className="p-2 text-xs">{p.branch}{p.invoice_number && <Badge variant="secondary" className="ml-1 text-xs">{p.invoice_number}</Badge>}</td>
                      <td className="p-2 text-xs text-right font-medium">{CUR} {p.amount.toFixed(2)}</td>
                      <td className="p-2 text-xs text-right text-error">{p.discount > 0 ? `${CUR} ${p.discount.toFixed(2)}` : '-'}</td>
                      <td className="p-2 text-xs">
                        {p.payments?.map((pm, j) => (
                          <span key={j} className="inline-block mr-1"><Badge variant="secondary" className="text-xs capitalize">{pm.mode}: {CUR} {pm.amount.toFixed(0)}</Badge></span>
                        ))}
                      </td>
                      <td className="p-2 text-xs text-right">
                        {p.credit > 0 ? <span className="font-bold text-warning">{CUR} {p.credit.toFixed(2)}</span> : '-'}
                        {p.credit_received > 0 && <div className="text-success text-xs">Received: {CUR} {p.credit_received.toFixed(2)}</div>}
                      </td>
                    </tr>
                    {p.items?.length > 0 && (
                      <tr className="bg-muted/50"><td colSpan={6} className="px-4 py-1">
                        <div className="flex gap-2 flex-wrap">{p.items.map((item, j) => (
                          <span key={j} className="text-xs bg-white px-2 py-0.5 rounded border">{item.description} x{item.quantity} @ {CUR} {item.unit_price}</span>
                        ))}</div>
                      </td></tr>
                    )}
                    </React.Fragment>
                  ))}{customerReport.purchases.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No purchases</td></tr>}</tbody></table>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* 3-step Customers walkthrough — opened from the info callout. */}
        <CustomersTour open={showCustomersTour} onOpenChange={setShowCustomersTour} />
        {/* iter571 — one-click Reconcile dialog for customers */}
        <CustomerReconcileDialog
          customer={reconcileCustomer}
          onClose={() => setReconcileCustomer(null)}
          onOpenReceive={(c) => {
            setReconcileCustomer(null);
            setReceivingCustomer({ ...c, credit_balance: getBalance(c.id).credit_balance });
            setReceiveData({ payment_mode: 'cash', amount: '' });
            setShowReceiveDialog(true);
          }}
        />
        {/* iter420 — Ledger export dialog (single customer or all customers) */}
        <LedgerExportDialog
          open={ledgerExportOpen}
          onOpenChange={setLedgerExportOpen}
          mode={ledgerExportMode}
          customer={ledgerExportCustomer}
        />
      </div>
    </DashboardLayout>
  );
}
